// Public menu layout — error screens, theme resolution, and main render.
import { useTranslation } from "react-i18next";
import { findProductBySlug } from "@/utils/productSlug";
import { OrderConfirmation } from "@/components/public/OrderConfirmation";
import { PublicMenuContent } from "@/components/public/PublicMenuContent";
import { PublicMenuDialogs } from "@/components/public/PublicMenuDialogs";
import { ProductLandingMode } from "@/components/public/ProductLandingMode";
import { BuffetRulesModal } from "@/components/public/BuffetRulesModal";
import { TableHubContainer } from "@/components/public/TableHubContainer";
import { computeMenuTheme, fmtPrice } from "@/utils/publicMenuHelpers";

export function PublicMenuLayout({
  restaurant, restaurantId, isSuspended, suspendedMessage,
  theme, BACKEND_BASE, products, categories, combos,
  isTableOrder, restaurantStatus, smartPricing, schedulingMode,
  deepLinkSlug, isRtl, landingModeActive, setLandingModeActive,
  linkedOfferPreview,
  isPayAtEndMode, tableNumber, tableName, tableId,
  tablePayAtEnd, tablePaymentEnabled, paymentAtTableEnabled,
  fetchMenu, cart, setCart, cartTotal, cartCount,
  addToCart, addToCartSilent, addSuggestionToCart, updateQuantity,
  suggestions, trackUpsell, showSuggestions, setShowSuggestions,
  checkoutSuggestions, setCheckoutSuggestions,
  checkout, customerSession, tableSession, buffetRules, API,
}) {
  const { t } = useTranslation();

  // Buffet rules gate: only products flagged requires_buffet_rules are blocked
  // until the customer accepts the rules (table/QR flow only — normal products,
  // delivery and takeaway are never affected).
  const guardProduct = buffetRules?.guardProductAction || ((fn) => fn);
  const guardCart = buffetRules?.guardCartAction || ((fn) => fn);
  const guardedAddToCart = guardProduct(addToCart);
  const guardedAddToCartSilent = guardProduct(addToCartSilent);
  const guardedAddSuggestionToCart = guardProduct(addSuggestionToCart);
  const guardedOpenCheckout = guardCart(checkout.openCheckout);
  const guardedSubmitOrder = guardCart(checkout.submitOrder);

  if (!restaurant && !isSuspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf9f6]">
        <div className="text-center max-w-sm px-6">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-lg font-bold text-gray-800 mb-2">{t("publicMenu.errors.restaurantNotFound")}</h1>
          <p className="text-gray-500 text-sm">{t("publicMenu.errors.menuUnavailableNow")}</p>
        </div>
      </div>
    );
  }

  const { tc, isDark, cardBg, cardBorder, catColor, priceColor, logoUrl, logoClass, bannerVideoUrl, allAllergens } =
    computeMenuTheme({ theme, BACKEND_BASE, products, restaurant });
  const fmt = (amount) => fmtPrice(amount, restaurant?.currency || "EUR");

  if (isSuspended) {
    return (
      <div className="min-h-screen bg-[#faf9f6] flex items-center justify-center px-6" data-testid="menu-closed-screen">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">{restaurant?.name || t("restaurant.title")}</h1>
          <p className="text-gray-500 mb-6">{suspendedMessage}</p>
        </div>
      </div>
    );
  }

  if (checkout.orderConfirmed) {
    return (
      <OrderConfirmation
        orderConfirmed={checkout.orderConfirmed} restaurant={restaurant} customerName={checkout.customerName}
        confirmedCart={checkout.confirmedCart} tableName={tableName} tableNumber={tableNumber}
        fullAddress={checkout.fullAddress} paymentMethod={checkout.paymentMethod} changeFor={checkout.changeFor}
        restaurantId={restaurantId} themeColor={tc.primary}
        onNewOrder={() => { checkout.setOrderConfirmed(null); checkout.setConfirmedCart([]); fetchMenu(); }}
      />
    );
  }

  const isOpen = restaurantStatus !== 'closed';

  return (
    <div className="min-h-screen" style={{ backgroundColor: tc.bg }} dir={isRtl ? "rtl" : "ltr"} data-testid="public-menu">
      {isTableOrder && !paymentAtTableEnabled && (
        <div className="sticky top-0 z-50 w-full bg-amber-500 text-white text-xs font-semibold text-center py-2 px-4 flex items-center justify-center gap-2" data-testid="prepayment-required-banner">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.538-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {t("publicMenu.prepaymentRequired")}
        </div>
      )}
      <PublicMenuContent
        restaurant={restaurant} restaurantId={restaurantId} isTableOrder={isTableOrder}
        menuSearchBanners={theme?.menu_search_banners || []}
        restaurantStatus={restaurantStatus} smartPricing={smartPricing}
        schedulingMode={schedulingMode} deepLinkSlug={deepLinkSlug} suppressAutoOpen={landingModeActive}
        tc={tc} isDark={isDark} cardBg={cardBg} cardBorder={cardBorder}
        catColor={catColor} priceColor={priceColor} logoUrl={logoUrl} logoClass={logoClass} bannerVideoUrl={bannerVideoUrl}
        tableName={tableName} tableNumber={tableNumber}
        customerSession={customerSession.customerSession}
        showReorderBanner={customerSession.showReorderBanner}
        setShowReorderBanner={customerSession.setShowReorderBanner}
        setShowAuthModal={customerSession.setShowAuthModal}
        handleReorder={customerSession.handleReorder}
        customerPhone={checkout.customerPhone}
        tablePayAtEnd={isPayAtEndMode} tableSession={tableSession.tableSession}
        billRequested={tableSession.billRequested} showCheckout={checkout.showCheckout}
        orderConfirmed={checkout.orderConfirmed} requestBill={tableSession.requestBill}
        refreshTableSession={tableSession.refreshTableSession}
        setShowTableOrdersModal={tableSession.setShowTableOrdersModal}
        requestingBill={tableSession.requestingBill}
        categories={categories} products={products} combos={combos}
        cart={cart} setCart={setCart} cartTotal={cartTotal} cartCount={cartCount}
        addToCart={guardedAddToCart} addToCartSilent={guardedAddToCartSilent} updateQuantity={updateQuantity}
        suggestions={suggestions} trackUpsell={trackUpsell}
        openCheckout={guardedOpenCheckout}
        setShowReservation={checkout.setShowReservation}
        setReservationConfirmed={checkout.setReservationConfirmed}
        setShowAllergenInfo={checkout.setShowAllergenInfo}
        fmt={fmt} API={API}
      />
      <PublicMenuDialogs
        showSuggestions={showSuggestions} setShowSuggestions={setShowSuggestions}
        suggestions={suggestions} addSuggestionToCart={guardedAddSuggestionToCart} trackUpsell={trackUpsell}
        categories={categories} tc={tc} fmt={fmt}
        showCheckout={checkout.showCheckout} setShowCheckout={checkout.setShowCheckout}
        restaurant={restaurant} restaurantId={restaurantId} isTableOrder={isTableOrder} isOpen={isOpen}
        tableName={tableName} tableNumber={tableNumber}
        tablePayAtEnd={isPayAtEndMode} tablePaymentEnabled={tablePaymentEnabled} paymentAtTableEnabled={paymentAtTableEnabled}
        cart={cart} cartTotal={cartTotal} smartPricing={smartPricing}
        checkoutStep={checkout.checkoutStep} setCheckoutStep={checkout.setCheckoutStep}
        orderType={checkout.orderType} setOrderType={checkout.setOrderType}
        deliveryAddress={checkout.deliveryAddress} setDeliveryAddress={checkout.setDeliveryAddress}
        deliveryLat={checkout.deliveryLat} setDeliveryLat={checkout.setDeliveryLat}
        deliveryLng={checkout.deliveryLng} setDeliveryLng={checkout.setDeliveryLng}
        addressNumber={checkout.addressNumber} setAddressNumber={checkout.setAddressNumber}
        addressComplement={checkout.addressComplement} setAddressComplement={checkout.setAddressComplement}
        addressBarrio={checkout.addressBarrio} setAddressBarrio={checkout.setAddressBarrio}
        fullAddress={checkout.fullAddress}
        customerName={checkout.customerName} setCustomerName={checkout.setCustomerName}
        customerPhone={checkout.customerPhone} setCustomerPhone={checkout.setCustomerPhone}
        orderNotes={checkout.orderNotes} setOrderNotes={checkout.setOrderNotes}
        couponCode={checkout.couponCode} setCouponCode={checkout.setCouponCode}
        referralDiscount={checkout.referralDiscount} setReferralDiscount={checkout.setReferralDiscount}
        loyaltyReward={checkout.loyaltyReward} setLoyaltyReward={checkout.setLoyaltyReward}
        paymentMethod={checkout.paymentMethod} setPaymentMethod={checkout.setPaymentMethod}
        changeFor={checkout.changeFor} setChangeFor={checkout.setChangeFor}
        scheduleDate={checkout.scheduleDate} setScheduleDate={checkout.setScheduleDate}
        scheduleTime={checkout.scheduleTime} setScheduleTime={checkout.setScheduleTime}
        schedulingMode={restaurant?.scheduling_config?.mode || "allow"}
        getAvailableDates={checkout.getAvailableDates} getTimeSlots={checkout.getTimeSlots}
        checkoutSuggestions={checkoutSuggestions} setCheckoutSuggestions={setCheckoutSuggestions}
        addToCartSilent={guardedAddToCartSilent} submitting={checkout.submitting} submitOrder={guardedSubmitOrder}
        showAllergenInfo={checkout.showAllergenInfo} setShowAllergenInfo={checkout.setShowAllergenInfo}
        allAllergens={allAllergens} logoUrl={logoUrl} logoClass={logoClass} isDark={isDark}
        showReservation={checkout.showReservation} setShowReservation={checkout.setShowReservation}
        reservationData={checkout.reservationData} setReservationData={checkout.setReservationData}
        reservationConfirmed={checkout.reservationConfirmed}
        setReservationConfirmed={checkout.setReservationConfirmed}
        reservationSubmitting={checkout.reservationSubmitting} submitReservation={checkout.submitReservation}
        reservationSlotAlternatives={checkout.reservationSlotAlternatives}
        selectReservationAlternative={checkout.selectReservationAlternative}
        showAuthModal={customerSession.showAuthModal} setShowAuthModal={customerSession.setShowAuthModal}
        customerSession={customerSession.customerSession}
        handleCustomerLogin={customerSession.handleCustomerLogin}
        handleCustomerLogout={customerSession.handleCustomerLogout}
        showTrackingModal={customerSession.showTrackingModal}
        setShowTrackingModal={customerSession.setShowTrackingModal}
        trackingOrderId={customerSession.trackingOrderId}
        showTableSessionModal={tableSession.showTableSessionModal}
        setShowTableSessionModal={tableSession.setShowTableSessionModal}
        tableSessionLoading={tableSession.tableSessionLoading}
        startTableSession={tableSession.startTableSession}
        handleViewOnly={tableSession.handleViewOnly}
        showTableOrdersModal={tableSession.showTableOrdersModal}
        setShowTableOrdersModal={tableSession.setShowTableOrdersModal}
        tableSession={tableSession.tableSession}
        requestBill={tableSession.requestBill} requestingBill={tableSession.requestingBill}
        billRequested={tableSession.billRequested} setBillRequested={tableSession.setBillRequested}
        setTableSession={tableSession.setTableSession} tableId={tableId}
        setCart={setCart} updateQuantity={updateQuantity}
      />
      <TableHubContainer
        API={API} restaurant={restaurant} restaurantId={restaurantId}
        isTableOrder={isTableOrder} tableId={tableId}
        tableName={tableName} tableNumber={tableNumber}
        tableSession={tableSession.tableSession}
        sessionModalOpen={tableSession.showTableSessionModal}
        themeColor={tc.primary} fmt={fmt}
      />
      {buffetRules?.buffetRulesActive && (
        <BuffetRulesModal
          open={buffetRules.showBuffetRulesModal}
          onAccept={buffetRules.acceptBuffetRules}
          onBack={buffetRules.closeBuffetRulesModal}
          isLoading={buffetRules.acceptingRules}
          themeColor={tc.primary}
        />
      )}
      {landingModeActive && deepLinkSlug && products.length > 0 && (() => {
        const lp = findProductBySlug(products, deepLinkSlug);
        if (!lp) return null;
        return (
          <ProductLandingMode
            product={lp} restaurant={restaurant} fmt={fmt} tc={tc} isDark={isDark}
            offerPreview={linkedOfferPreview}
            addToCart={guardedAddToCart} openCheckout={guardedOpenCheckout}
            onClose={() => {
              setLandingModeActive(false);
              if (restaurant?.slug) window.history.replaceState({}, '', `/m/${restaurant.slug}`);
            }}
            onCustomize={() => setLandingModeActive(false)}
          />
        );
      })()}
    </div>
  );
}
